import {
  UserCircleIcon,
  PlusCircleIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ClockIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'

export default function VisitorsHelp() {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex items-center mb-6">
          <UserCircleIcon className="h-8 w-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Visitor Management</h1>
        </div>

        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            The Visitor Management system provides comprehensive tools for scheduling, tracking, and managing
            visitor access to your premises. Track visitor arrivals, generate passes, and maintain detailed records.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Key Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <PlusCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Visitor Scheduling</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Schedule visitor appointments with detailed information including purpose, company, and assigned staff members.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <CalendarDaysIcon className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Date Navigation</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Navigate through different dates to view scheduled visitors and track daily visitor activity.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <DocumentTextIcon className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Pass Generation</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Generate and print visitor passes with QR codes and visitor information for secure access control.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <ClockIcon className="h-6 w-6 text-orange-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Arrival Tracking</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Monitor visitor arrival and departure times with real-time status updates and photo capture.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Main Dashboard</h2>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Header Controls</h3>

            <div className="bg-white rounded-lg p-4 border border-gray-200 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h4 className="font-medium text-gray-900">Visitors</h4>

                {/* Date Navigation */}
                <div className="flex items-center gap-2">
                  <button className="inline-flex items-center rounded-md bg-gray-200 px-2 py-2 text-gray-700 opacity-75 cursor-not-allowed">
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white">
                    <CalendarDaysIcon className="h-5 w-5 mr-1" />
                    Today
                  </button>
                  <button className="inline-flex items-center rounded-md bg-gray-200 px-2 py-2 text-gray-700 opacity-75 cursor-not-allowed">
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-700 font-medium ml-2">Mon, 14 Oct 2025</span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white opacity-75 cursor-not-allowed">
                    <PlusCircleIcon className="h-5 w-5 mr-2" />
                    Create Visitor
                  </button>
                  <button className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white opacity-75 cursor-not-allowed">
                    <UserCircleIcon className="h-5 w-5 mr-2" />
                    Visitor Login
                  </button>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Visitor Status System</h2>

          <div className="space-y-6 mb-8">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-4 h-4 bg-sky-400 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-900">Awaiting Arrival</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">Visitors who are scheduled but haven't arrived yet.</p>
              <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <UserCircleIcon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">John Smith</h4>
                      <p className="text-sm text-gray-600">ABC Corporation</p>
                      <div className="flex gap-4 text-xs text-gray-500 mt-1">
                        <span>Visit Time: 10:00</span>
                        <span>Staff: Sarah Johnson</span>
                      </div>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-3 py-1 rounded-lg bg-sky-100 text-sky-800 text-xs font-medium">
                    Awaiting Arrival
                  </span>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-4 h-4 bg-emerald-400 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-900">Currently On Site</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">Visitors who have arrived and are currently on the premises.</p>
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="/api/placeholder/48/48"
                      alt="Profile"
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">Jane Doe</h4>
                      <p className="text-sm text-gray-600">XYZ Industries</p>
                      <div className="flex gap-4 text-xs text-gray-500 mt-1">
                        <span>Arrived: 09:45</span>
                        <span>Staff: Mike Wilson</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-lg bg-red-100 text-red-800 text-xs font-medium">
                      Pass not printed
                    </span>
                    <button className="inline-flex items-center px-3 py-1 rounded-lg bg-white text-emerald-700 border border-emerald-600 text-xs font-medium opacity-75 cursor-not-allowed">
                      Generate Pass
                    </button>
                    <button className="inline-flex items-center px-3 py-1 rounded-lg bg-red-700 text-white text-xs font-medium opacity-75 cursor-not-allowed">
                      Left
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-4 h-4 bg-red-400 rounded-full mr-3"></div>
                <h3 className="text-lg font-semibold text-gray-900">Visit Completed</h3>
              </div>
              <p className="text-gray-600 text-sm mb-3">Visitors who have completed their visit and left the premises.</p>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="/api/placeholder/48/48"
                      alt="Profile"
                      className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">Bob Johnson</h4>
                      <p className="text-sm text-gray-600">Tech Solutions Ltd</p>
                      <div className="flex gap-4 text-xs text-gray-500 mt-1">
                        <span>Arrived: 14:30</span>
                        <span>Left: 16:45</span>
                        <span>Staff: Lisa Brown</span>
                      </div>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-3 py-1 rounded-lg bg-white text-red-700 border border-red-600 text-xs font-medium">
                    Left at 16:45
                  </span>
                </div>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Creating Visitors</h2>

          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Visitor Creation Form</h3>

            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                    placeholder="Enter visitor name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                  <input
                    type="time"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Visit</label>
                  <input
                    type="text"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                    placeholder="Enter visit purpose"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Staff</label>
                  <select
                    multiple
                    className="w-full p-2 border border-gray-300 rounded-md text-sm opacity-75 cursor-not-allowed"
                    size="3"
                  >
                    <option>Sarah Johnson - HR Manager</option>
                    <option>Mike Wilson - IT Support</option>
                    <option>Lisa Brown - Sales Director</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple staff members</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md text-sm font-medium opacity-75 cursor-not-allowed">
                  Save Visitor
                </button>
                <button className="flex-1 bg-gray-400 text-white py-2 px-4 rounded-md text-sm font-medium opacity-75 cursor-not-allowed">
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Use</h2>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Schedule Visitors</h4>
                <p className="text-gray-600 text-sm">Create visitor appointments by filling out the visitor details form with name, company, date, time, and assigned staff.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Navigate Dates</h4>
                <p className="text-gray-600 text-sm">Use the date navigation controls to view visitors scheduled for different days or quickly jump to today.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Track Arrivals</h4>
                <p className="text-gray-600 text-sm">Monitor visitor status - awaiting arrival, currently on site, or visit completed - with automatic photo capture on arrival.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Generate Passes</h4>
                <p className="text-gray-600 text-sm">Generate and print visitor passes for arrived visitors, with automatic PDF download and pass status tracking.</p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-semibold text-sm">5</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Record Departures</h4>
                <p className="text-gray-600 text-sm">Mark visitors as departed when they leave, completing the visit record with departure timestamp.</p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Visitor Preparation</h4>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>• Schedule visits in advance with complete details</li>
                    <li>• Assign appropriate staff members for the visit</li>
                    <li>• Include specific visit purposes and expected duration</li>
                    <li>• Verify company information for security</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Access Control</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>• Generate passes immediately upon visitor arrival</li>
                    <li>• Monitor visitor status throughout their stay</li>
                    <li>• Record departure times for complete tracking</li>
                    <li>• Maintain visitor photos for security records</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <BuildingOfficeIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">Security & Compliance</h4>
                <ul className="text-yellow-800 text-sm space-y-1 mb-3">
                  <li>• Always verify visitor identity upon arrival</li>
                  <li>• Ensure all visitors sign in and out properly</li>
                  <li>• Maintain detailed visit logs for security audits</li>
                  <li>• Follow company visitor policy and procedures</li>
                </ul>
                <div className="bg-white rounded-lg p-3 border border-yellow-300">
                  <p className="text-yellow-800 text-sm">
                    <strong>Important:</strong> Visitor management helps maintain security while providing excellent guest experience.
                    Always follow your organization's visitor policy and report any security concerns immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}